import {
	type MouseEvent as ReactMouseEvent,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
	addEdge,
	Connection,
	ConnectionLineType,
	Edge as XyFlowEdge,
	Node,
	NodeChange,
	useEdgesState,
	useNodesState,
	useUpdateNodeInternals,
} from '@xyflow/react';
import debounce from 'lodash/debounce';
import * as dagre from '@dagrejs/dagre';

import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import {
	getGroupStructurePositions,
	updateCapChartPositions,
} from '@dealstack/frontend/app/services/CapTableData.service';
import { edgeTypes, edgeTypesFast, nodeTypes } from './chart.model';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { CapChartPageContext } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChartPage';
import {
	getLayoutedElements,
	getNewEdgePosition,
	makeEdgeLabelValues,
	makeTargetHandleId,
} from './chart.utils';
import {
	CapChartEdgeData,
	NodeData,
} from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/chart.model.ts';
import {
	GetGroupStructureDataRes,
	GetGroupStructurePositionsDataRes,
} from '@dealstack/shared/lib/generated';

const SMART_EDGE_PERFORMANCE_THRESHOLD = 300;

export const useCapChartController = () => {
	const [nodes, setNodes, onNodesChange] = useNodesState<Node<NodeData>>([]);
	const [edges, setEdges, onEdgesChange] = useEdgesState<XyFlowEdge<CapChartEdgeData>>([]);
	// The chart does not allow us to easily track the min and max x positions of the nodes
	const [isDataLoaded, setIsDataLoaded] = useState(false);
	const [minNodeX, setMinNodeX] = useState(0);
	const [maxNodeX, setMaxNodeX] = useState(0);

	const capChartContext = useContext(CapChartPageContext);
	const { rfInstance, chartSettings, capChartData } = capChartContext.capChartData;
	const { isChartLoading } = capChartContext;
	const updateNodeInternals = useUpdateNodeInternals();

	const useSmartEdge = useMemo(() => edges.length < SMART_EDGE_PERFORMANCE_THRESHOLD, [edges]);
	const connectionLineType = useMemo(
		() => (useSmartEdge ? ConnectionLineType.SmoothStep : ConnectionLineType.Step),
		[useSmartEdge]
	);

	useEffect(() => {
		if (nodes.length > 0) {
			setMinNodeX(Math.min(...nodes.map((n) => n.position.x)));
			setMaxNodeX(Math.max(...nodes.map((n) => n.position.x)));
		}
	}, [nodes]);

	const urlParams = useParams();
	const dealId = urlParams.dealId;

	const getGroupStructurePositionsRequest = useQuery({
		queryKey: [
			API_PATHS.CAP_GROUP_STRUCTURE_POSITIONS_POST,
			{
				body: {
					dealId,
				},
			},
		],

		queryFn: () => getGroupStructurePositions(API_PATHS.CAP_GROUP_STRUCTURE_POSITIONS_POST),
	});

	const updateCapChartPositionsRequest = useMutation({
		mutationFn: ({ positions }: { positions: unknown }) => {
			if (!dealId) {
				throw new Error('dealId is required to update cap chart positions');
			}

			return updateCapChartPositions({
				positions,
			});
		},
	});

	const groupStructurePositions = getGroupStructurePositionsRequest.data;
	const isLoading = isChartLoading || getGroupStructurePositionsRequest.isFetching;

	const dagreGraph = useMemo(() => {
		const graph = new dagre.graphlib.Graph();
		graph.setDefaultEdgeLabel(() => ({}));
		graph.setGraph({
			rankdir: 'BT',
		});
		return graph;
	}, []);

	useEffect(() => {
		if (!capChartData || !groupStructurePositions || isLoading) {
			return;
		}

		if (!capChartData.nodes) {
			return;
		}

		if (groupStructurePositions.positions?.nodes) {
			calculateNodesWithHashPositions(capChartData, groupStructurePositions);
			setIsDataLoaded(true);
		} else {
			onReset(true);
			setIsDataLoaded(true);
		}
	}, [capChartData, groupStructurePositions, isLoading]);

	const calculateNodesWithHashPositions = (
		capChartData: GetGroupStructureDataRes,
		hashedPositions: GetGroupStructurePositionsDataRes
	) => {
		const { nodes: hashedNodes } = hashedPositions?.positions || {};
		const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
			capChartData.nodes,
			capChartData.edges,
			dagreGraph,
			chartSettings
		);

		const hashedNodesMap = (hashedNodes || []).reduce((acc, node) => {
			acc[node.id] = node;
			return acc;
		}, {});

		const updatedNodes = layoutedNodes.map((node) => {
			const hashedNode = hashedNodesMap[node.id];

			if (!hashedNode) {
				return node;
			}

			return {
				...node,
				position: {
					x: hashedNode.position?.x,
					y: hashedNode.position?.y,
				},
			};
		});

		setNodes(updatedNodes);
		setEdges(layoutedEdges);

		layoutedEdges.forEach((edge) => {
			updateEdge(edge.id, edge);
		});
	};

	const onReset = useCallback(
		(hideToast = false) => {
			if (capChartData?.nodes && capChartData?.edges) {
				const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
					capChartData.nodes,
					capChartData.edges,
					dagreGraph,
					chartSettings
				);

				setNodes(layoutedNodes);
				setEdges(layoutedEdges);

				if (!hideToast) {
					showToastV2({
						message: 'Chart has been reset to default layout.',
						state: 'success',
					});
				}
			}
		},
		[capChartData, chartSettings]
	);

	const onConnect = useCallback(
		(params: Connection) =>
			setEdges((eds) =>
				addEdge({ ...params, type: ConnectionLineType.Step, animated: true }, eds)
			),
		[]
	);

	const onSave = useCallback(() => {
		if (rfInstance) {
			const flow = rfInstance.toObject();

			updateCapChartPositionsRequest.mutate(
				{
					positions: flow,
				},
				{
					onSuccess: () => {
						showToastV2({
							message: 'Saved chart positions.',
							state: 'success',
						});
					},
					onError: () => {
						showToastV2({
							message: 'Failed to save the chart positions.',
							state: 'error',
						});
					},
				}
			);
		}
	}, [rfInstance, updateCapChartPositionsRequest]);

	useEffect(() => {
		if (!edges.length) {
			return;
		}
		setEdges(
			edges.map((edge) =>
				edge.data
					? {
							...edge,
							data: {
								...edge.data,
								labelValues: makeEdgeLabelValues(edge.data, chartSettings),
							},
						}
					: edge
			)
		);
	}, [chartSettings]);

	const handleEdgeHover = useCallback(
		(edge: XyFlowEdge, isHovered: boolean, mouseEvent: ReactMouseEvent) => {
			if (mouseEvent.buttons === 1) {
				return;
			}
			const edgeType = edge?.type || 'ownership';
			const className = isHovered ? `${edgeType} hovered` : '';

			setNodes((nds) =>
				nds.map((n) =>
					n.id === edge.source || n.id === edge.target ? { ...n, className } : n
				)
			);
			setEdges((eds) =>
				eds.map((e) => {
					if (e.id !== edge.id) {
						return e;
					}

					const label = document.querySelector(
						`div.edge-label[data-edge-id="${edge?.id}"]`
					);

					if (isHovered) {
						label?.classList.add('hovered');
					} else {
						label?.classList?.remove('hovered');
					}

					return {
						...e,
						className,
					};
				})
			);
		},
		[]
	);

	const onEdgeMouseEnter = useCallback(
		(event: ReactMouseEvent, edge: XyFlowEdge) => handleEdgeHover(edge, true, event),
		[handleEdgeHover]
	);

	const onEdgeMouseLeave = useCallback(
		(event: ReactMouseEvent, edge: XyFlowEdge) => handleEdgeHover(edge, false, event),
		[handleEdgeHover]
	);

	const { updateNodeData, updateEdge, getNode } = rfInstance;

	const debouncedHandleNodeChanges = useRef(
		debounce(
			({
				nodeChanges,
				nds,
				eds,
				minNdX,
				maxNdX,
			}: {
				nodeChanges: NodeChange<Node<NodeData>>[];
				nds: Node<NodeData>[];
				eds: XyFlowEdge<CapChartEdgeData>[];
				minNdX: number;
				maxNdX: number;
			}) => {
				nodeChanges.forEach((change) => {
					if (change.type === 'position') {
						if (!nds.length || !eds.length) {
							return;
						}

						const loanEdges = eds.filter((e) => e.type === 'debt');
						const eventNode = getNode(change.id) as Node<NodeData>;

						if (!loanEdges.length) {
							return;
						}

						if (eventNode) {
							const { position, sourceLoanEdges } =
								getNewEdgePosition(eventNode, minNdX, maxNdX, loanEdges) || {};

							if (position && sourceLoanEdges) {
								updateNodeData(eventNode.id, {
									loanData: {
										...eventNode.data.loanData,
										sourcePosition: position,
									},
								});

								for (const e of sourceLoanEdges) {
									const targetHandle = makeTargetHandleId(
										true,
										e.source,
										e.target,
										nds,
										position
									);

									updateEdge(e.id, {
										targetHandle,
									});
								}

								updateNodeInternals(eventNode.id);
							}
						}
					}
				});
			},
			200
		)
	);

	const handleNodeChanges = (nodeChanges: NodeChange<Node<NodeData>>[]) => {
		onNodesChange(nodeChanges);
		debouncedHandleNodeChanges.current({
			nodeChanges,
			nds: nodes,
			eds: edges,
			minNdX: minNodeX,
			maxNdX: maxNodeX,
		});
	};

	return {
		nodes,
		edges,
		nodeTypes,
		edgeTypes: useSmartEdge ? edgeTypes : edgeTypesFast,
		isDataLoaded,
		onEdgeMouseEnter,
		onEdgeMouseLeave,
		onNodesChange: handleNodeChanges,
		onEdgesChange,
		onConnect,
		connectionLineType,
		onSave,
		onReset,
		setEdges,
		updateCapChartPositionsRequest,
		groupStructurePositions: getGroupStructurePositionsRequest.data,
		isPositionsLoading: getGroupStructurePositionsRequest.isFetching,
	};
};
